import { useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'react-feather';
import { MAX_CC_TICKET, emailValido, esCandidatoCc } from '../../features/tickets';

// Destinatarios de la respuesta publica: "Para" (el solicitante, fijo) y "CC"
// (lista del ticket, editable). Cada cambio de CC se guarda al momento en el
// ticket (onChange devuelve una promesa), asi que aplica a esta respuesta y a
// las siguientes.
const TicketCcEditor = ({ solicitante, cc, onChange, guardando }) => {
  const [valor, setValor] = useState('');
  const [error, setError] = useState('');

  const guardar = async (emails) => {
    setError('');
    try {
      await onChange(emails);
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo guardar el CC.');
      return false;
    }
  };

  const anadir = async () => {
    // Admite pegar varios separados por coma, punto y coma o espacios.
    const nuevos = valor
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    if (nuevos.length === 0) return;

    const invalido = nuevos.find((e) => !emailValido(e));
    if (invalido) {
      setError(`"${invalido}" no es un email válido.`);
      return;
    }
    const aceptados = [];
    nuevos.forEach((e) => {
      if (esCandidatoCc(e, { solicitante, cc: [...cc, ...aceptados] })) aceptados.push(e);
    });
    if (aceptados.length === 0) {
      setError('Ese correo ya recibe las respuestas.');
      return;
    }
    if (cc.length + aceptados.length > MAX_CC_TICKET) {
      setError(`Máximo ${MAX_CC_TICKET} correos en copia.`);
      return;
    }
    if (await guardar([...cc, ...aceptados])) setValor('');
  };

  const quitar = (email) => guardar(cc.filter((c) => c !== email));

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      anadir();
    }
  };

  return (
    <div className="tickets-cc">
      <div className="tickets-cc__fila">
        <span className="tickets-cc__label">Para</span>
        <span className="tickets-cc__para">{solicitante || '—'}</span>
      </div>
      <div className="tickets-cc__fila">
        <span className="tickets-cc__label">CC</span>
        <ul className="tickets-cc__lista" aria-label="Correos en copia">
          {cc.map((email) => (
            <li key={email} className="tickets-tag">
              {email}
              <button
                type="button"
                className="tickets-tag__remove"
                aria-label={`Quitar ${email} de CC`}
                onClick={() => quitar(email)}
                disabled={guardando}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </li>
          ))}
          <li className="tickets-cc__input-wrap">
            <input
              type="text"
              className="tickets-cc__input"
              value={valor}
              onChange={(e) => {
                setValor(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={onKeyDown}
              onBlur={anadir}
              placeholder={cc.length ? 'Añadir otro…' : 'Añadir correo en copia…'}
              aria-label="Añadir correo en copia"
              disabled={guardando}
            />
          </li>
        </ul>
      </div>
      {error && <p className="tickets-composer__error">{error}</p>}
    </div>
  );
};

TicketCcEditor.propTypes = {
  solicitante: PropTypes.string,
  cc: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  guardando: PropTypes.bool,
};

TicketCcEditor.defaultProps = {
  solicitante: '',
  cc: [],
  guardando: false,
};

export default TicketCcEditor;
